
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateNumericInput } from "./utilities/validateInput";
import { useState } from "react";

interface NutritionalFieldsProps {
  calories: string;
  carbs: string;
  protein: string;
  fat: string;
  onChange: (field: string, value: string) => void;
}

export const NutritionalFields = ({ calories, carbs, protein, fat, onChange }: NutritionalFieldsProps) => {
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({
    calories: null,
    carbs: null,
    protein: null,
    fat: null
  });

  const handleChange = (field: string, value: string) => {
    // First check if the input contains any non-numeric characters (except decimal)
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      setErrors(prev => ({ ...prev, [field]: 'Please enter numbers only' }));
      return;
    }

    const error = validateNumericInput(value, 'nutritional');
    setErrors(prev => ({ ...prev, [field]: error }));
    if (!error) {
      onChange(field, value);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="calories" className="text-black">Calories</Label>
        <div>
          <Input
            id="calories"
            type="text"
            value={calories}
            onChange={(e) => handleChange("calories", e.target.value)}
            placeholder="e.g., 500"
            className={`h-12 rounded-xl text-black bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B] ${
              errors.calories ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {errors.calories && (
            <p className="text-sm text-red-500 mt-1">{errors.calories}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="carbs" className="text-black">Carbs (g)</Label>
        <div>
          <Input
            id="carbs"
            type="text"
            value={carbs}
            onChange={(e) => handleChange("carbs", e.target.value)}
            placeholder="e.g., 45.5"
            className={`h-12 rounded-xl text-black bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B] ${
              errors.carbs ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {errors.carbs && (
            <p className="text-sm text-red-500 mt-1">{errors.carbs}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="protein" className="text-black">Protein (g)</Label>
        <div>
          <Input
            id="protein"
            type="text"
            value={protein}
            onChange={(e) => handleChange("protein", e.target.value)}
            placeholder="e.g., 20.0"
            className={`h-12 rounded-xl text-black bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B] ${
              errors.protein ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {errors.protein && (
            <p className="text-sm text-red-500 mt-1">{errors.protein}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="fat" className="text-black">Fat (g)</Label>
        <div>
          <Input
            id="fat"
            type="text"
            value={fat}
            onChange={(e) => handleChange("fat", e.target.value)}
            placeholder="e.g., 15.5"
            className={`h-12 rounded-xl text-black bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B] ${
              errors.fat ? 'border-red-500 focus-visible:ring-red-500' : ''
            }`}
          />
          {errors.fat && (
            <p className="text-sm text-red-500 mt-1">{errors.fat}</p>
          )}
        </div>
      </div>
    </div>
  );
};
